import React from 'react';
import {
  // IT Icons
  Storage, Dns, Language, Security, Hub, Router as RouterIcon,
  Apps, Print, Devices as DevicesIcon,
  Computer, TouchApp, Sensors, Cloud, CloudQueue, CloudSync,
  AccountTree,

  // OT & Industrial Icons
  Factory, Analytics, PrecisionManufacturing, Timeline, SettingsInputComponent, 
  SettingsRemote, Schema, Inventory2, SmartToy, FilterAlt, Sync, Lan, East,
  DeveloperBoard,

  // IIoT & Edge Device Icons
  RssFeed, Adb, SignalCellularAlt, Construction, Android, TrackChanges, 
  Watch, Videocam, NetworkCheck, Wifi, Bluetooth, Cable
} from '@mui/icons-material';

// Helper type for expected properties
type ExpectedProperties = {
  basic: string[],
  technical: string[],
  business: string[]
};

// Default properties for unknown types
const defaultProperties: ExpectedProperties = {
  basic: ['CI Class', 'Operational Status', 'Environment'],
  technical: ['IP Address', 'FQDN', 'OS Version'],
  business: ['Support Group', 'Assigned To', 'Location']
};

/**
 * A centralized dictionary for all Configuration Item (CI) types.
 * This file is the single source of truth for CI labels, categories, icons, colors, and expected properties.
 * It includes standard IT, Operational Technology (OT), and Industrial IoT (IIoT) infrastructure.
 */
export const ciTypes = [
  // =================================================================
  // Servers - All server types including VMware
  // =================================================================
  { 
    type: 'PHYSICAL_SERVER', label: 'Physical Server', category: 'Servers', icon: <Dns />, color: '#2E7D32',
    expectedProperties: {
      basic: ['CI Class', 'Operational Status', 'Environment', 'Business Criticality'],
      technical: ['Management IP', 'OS', 'Manufacturer', 'Model', 'Serial Number', 'CPU Count', 'RAM'],
      business: ['Support Group', 'Assigned To', 'Cost Center', 'Location']
    }
  },
  { 
    type: 'VIRTUAL_MACHINE', label: 'Virtual Machine', category: 'Servers', icon: <Computer sx={{ transform: 'scale(0.85)'}} />, color: '#2E7D32',
    expectedProperties: {
      basic: ['CI Class', 'Operational Status', 'Environment', 'Business Criticality'],
      technical: ['IP Address', 'FQDN', 'OS', 'OS Version', 'CPU Count', 'RAM', 'Host Server'],
      business: ['Support Group', 'Assigned To', 'Cost Center']
    }
  },
  { 
    type: 'VCENTER_SERVER', label: 'vCenter Server', category: 'Servers', icon: <Dns />, color: '#2E7D32',
    expectedProperties: {
      basic: ['CI Class', 'Operational Status', 'Environment', 'Version'],
      technical: ['IP Address', 'FQDN', 'CPU Count', 'RAM', 'Build Number'],
      business: ['Support Group', 'Assigned To', 'Location']
    }
  },
  { 
    type: 'ESXI_HOST', label: 'ESXi Host', category: 'Servers', icon: <Dns />, color: '#2E7D32',
    expectedProperties: {
      basic: ['CI Class', 'Operational Status', 'Environment', 'Version'],
      technical: ['Management IP', 'FQDN', 'Manufacturer', 'Model', 'Serial Number', 'CPU Cores', 'RAM'],
      business: ['Support Group', 'Assigned To', 'Cost Center', 'Location']
    }
  },
  { 
    type: 'WEB_SERVER', label: 'Web Server', category: 'Servers', icon: <Language />, color: '#2E7D32',
    expectedProperties: {
      basic: ['CI Class', 'Operational Status', 'Environment', 'Business Criticality'],
      technical: ['IP Address', 'FQDN', 'OS', 'Web Server Software', 'Port', 'SSL Certificate'],
      business: ['Support Group', 'Assigned To', 'Cost Center', 'Location']
    }
  },
  { 
    type: 'APPLICATION', label: 'Application', category: 'Applications', icon: <Apps />, color: '#1565C0',
    expectedProperties: {
      basic: ['CI Class', 'Operational Status', 'Environment', 'Business Criticality'],
      technical: ['Version', 'Vendor', 'Technology Stack', 'Dependencies', 'Port'],
      business: ['Support Group', 'Assigned To', 'Cost Center', 'Business Owner']
    }
  },
  { 
    type: 'WEB_APPLICATION', label: 'Web Application', category: 'Applications', icon: <Language />, color: '#1565C0',
    expectedProperties: {
      basic: ['CI Class', 'Operational Status', 'Environment', 'Business Criticality'],
      technical: ['URL', 'Port', 'Version', 'Vendor', 'Technology Stack', 'Dependencies'],
      business: ['Support Group', 'Assigned To', 'Cost Center', 'Business Owner']
    }
  },
  { 
    type: 'DATABASE_INSTANCE', label: 'Database Instance', category: 'Databases', icon: <Storage />, color: '#E65100',
    expectedProperties: {
      basic: ['CI Class', 'Operational Status', 'Environment', 'Business Criticality'],
      technical: ['Database Name', 'Version', 'Port', 'Instance Name', 'Host Server', 'Backup Schedule', 'Connection String'],
      business: ['Support Group', 'Assigned To', 'Cost Center', 'Data Owner']
    }
  },
  { 
    type: 'HISTORIAN', label: 'Historian', category: 'Databases', icon: <Timeline />, color: '#E65100',
    expectedProperties: {
      basic: ['CI Class', 'Operational Status', 'Environment', 'Business Criticality'],
      technical: ['Database Name', 'Version', 'Port', 'Data Retention Period', 'Compression Ratio', 'Host Server'],
      business: ['Support Group', 'Assigned To', 'Cost Center', 'Data Owner']
    }
  },
  { 
    type: 'STORAGE_ARRAY', label: 'Storage Array (SAN/NAS)', category: 'Storage', icon: <Dns />, color: '#37474F',
    expectedProperties: {
      basic: ['CI Class', 'Operational Status', 'Environment', 'Business Criticality'],
      technical: ['Management IP', 'Total Capacity', 'Used Space', 'Protocol (iSCSI/FC/NFS/SMB)', 'RAID Level', 'Serial Number', 'Model'],
      business: ['Support Group', 'Assigned To', 'Cost Center', 'Data Owner']
    }
  },
  { 
    type: 'NETWORK_PRINTER', label: 'Network Printer', category: 'Peripherals', icon: <Print />, color: '#757575',
    expectedProperties: {
      basic: ['CI Class', 'Operational Status', 'Environment', 'Business Criticality'],
      technical: ['IP Address', 'Model', 'Serial Number', 'Driver Version', 'Paper Size'],
      business: ['Support Group', 'Assigned To', 'Cost Center', 'Location']
    }
  },
  {
    type: 'UNKNOWN_DEVICE', label: 'Unknown Device', category: 'Peripherals', icon: <DevicesIcon />, color: '#546E7A',
    expectedProperties: {
      basic: ['CI Class', 'Operational Status', 'Environment'],
      technical: ['Hostname', 'IP Address', 'Device Type'],
      business: ['Support Group', 'Assigned To', 'Location']
    }
  },
  { 
    type: 'ROUTER', label: 'Router', category: 'IT Network', icon: <RouterIcon />, color: '#6A1B9A',
    expectedProperties: {
      basic: ['CI Class', 'Operational Status', 'Environment', 'Business Criticality'],
      technical: ['Management IP', 'Firmware Version', 'Model', 'Serial Number', 'Interface Count'],
      business: ['Support Group', 'Assigned To', 'Cost Center', 'Location']
    }
  },
  { 
    type: 'SWITCH', label: 'Switch', category: 'IT Network', icon: <Hub />, color: '#6A1B9A',
    expectedProperties: {
      basic: ['CI Class', 'Operational Status', 'Environment', 'Business Criticality'],
      technical: ['Management IP', 'Firmware Version', 'Model', 'Serial Number', 'Port Count'],
      business: ['Support Group', 'Assigned To', 'Cost Center', 'Location']
    }
  },
  { 
    type: 'FIREWALL', label: 'Firewall', category: 'IT Network', icon: <Security />, color: '#6A1B9A',
    expectedProperties: {
      basic: ['CI Class', 'Operational Status', 'Environment', 'Business Criticality'],
      technical: ['Management IP', 'Firmware Version', 'Model', 'Serial Number', 'Policy Count'],
      business: ['Support Group', 'Assigned To', 'Cost Center', 'Location']
    }
  },
  { 
    type: 'LOAD_BALANCER', label: 'Load Balancer', category: 'IT Network', icon: <AccountTree />, color: '#6A1B9A',
    expectedProperties: {
      basic: ['CI Class', 'Operational Status', 'Environment', 'Business Criticality'],
      technical: ['Virtual IP', 'Port', 'Algorithm', 'Health Check', 'Backend Servers'],
      business: ['Support Group', 'Assigned To', 'Cost Center', 'Location']
    }
  },
  { 
    type: 'WAP', label: 'Wireless Access Point', category: 'IT Network', icon: <Wifi />, color: '#6A1B9A',
    expectedProperties: {
      basic: ['CI Class', 'Operational Status', 'Environment', 'Business Criticality'],
      technical: ['Management IP', 'Firmware Version', 'Model', 'Serial Number', 'SSID', 'Channel'],
      business: ['Support Group', 'Assigned To', 'Cost Center', 'Location']
    }
  },

  // =================================================================
  // Operational Technology (OT) Infrastructure
  // =================================================================

  // --- OT - Supervisory & Control ---
  { 
    type: 'SCADA_SERVER', label: 'SCADA Server', category: 'OT - Supervisory & Control', icon: <Analytics />, color: '#FF5722',
    expectedProperties: {
      basic: ['CI Class', 'Operational Status', 'Environment', 'Business Criticality'],
      technical: ['IP Address', 'FQDN', 'OS', 'SCADA Software', 'Version', 'Database'],
      business: ['Support Group', 'Assigned To', 'Cost Center', 'Location']
    }
  },
  { 
    type: 'HMI', label: 'HMI', category: 'OT - Supervisory & Control', icon: <TouchApp />, color: '#FF5722',
    expectedProperties: {
      basic: ['CI Class', 'Operational Status', 'Environment', 'Business Criticality'],
      technical: ['IP Address', 'FQDN', 'OS', 'HMI Software', 'Version', 'Screen Size'],
      business: ['Support Group', 'Assigned To', 'Cost Center', 'Location']
    }
  },
  { 
    type: 'HISTORIAN', label: 'Historian', category: 'OT - Supervisory & Control', icon: <Timeline />, color: '#FF5722',
    expectedProperties: {
      basic: ['CI Class', 'Operational Status', 'Environment', 'Business Criticality'],
      technical: ['IP Address', 'FQDN', 'OS', 'Historian Software', 'Version', 'Storage Capacity'],
      business: ['Support Group', 'Assigned To', 'Cost Center', 'Location']
    }
  },
  { 
    type: 'ENGINEERING_WORKSTATION', label: 'Engineering Workstation', category: 'OT - Supervisory & Control', icon: <PrecisionManufacturing />, color: '#FF5722',
    expectedProperties: {
      basic: ['CI Class', 'Operational Status', 'Environment', 'Business Criticality'],
      technical: ['IP Address', 'FQDN', 'OS', 'Engineering Software', 'Version', 'CPU Count', 'RAM'],
      business: ['Support Group', 'Assigned To', 'Cost Center', 'Location']
    }
  },
  { 
    type: 'MES', label: 'Manufacturing Execution System', category: 'OT - Supervisory & Control', icon: <Factory />, color: '#FF5722',
    expectedProperties: {
      basic: ['CI Class', 'Operational Status', 'Environment', 'Business Criticality'],
      technical: ['IP Address', 'FQDN', 'OS', 'MES Software', 'Version', 'Database'],
      business: ['Support Group', 'Assigned To', 'Cost Center', 'Location']
    }
  },
  { 
    type: 'DCS_CONTROLLER', label: 'DCS Controller', category: 'OT - Supervisory & Control', icon: <SettingsInputComponent />, color: '#FF5722',
    expectedProperties: {
      basic: ['CI Class', 'Operational Status', 'Environment', 'Business Criticality'],
      technical: ['IP Address', 'FQDN', 'Firmware Version', 'Model', 'Serial Number', 'I/O Count'],
      business: ['Support Group', 'Assigned To', 'Cost Center', 'Location']
    }
  },
  
  // --- OT - Field Devices & Controllers ---
  { 
    type: 'PLC', label: 'PLC', category: 'OT - Field Devices', icon: <DeveloperBoard />, color: '#FF9800',
    expectedProperties: {
      basic: ['CI Class', 'Operational Status', 'Environment', 'Business Criticality'],
      technical: ['IP Address', 'FQDN', 'Firmware Version', 'Model', 'Serial Number', 'I/O Count'],
      business: ['Support Group', 'Assigned To', 'Cost Center', 'Location']
    }
  },
  { 
    type: 'RTU', label: 'RTU', category: 'OT - Field Devices', icon: <SettingsRemote />, color: '#FF9800',
    expectedProperties: {
      basic: ['CI Class', 'Operational Status', 'Environment', 'Business Criticality'],
      technical: ['IP Address', 'FQDN', 'Firmware Version', 'Model', 'Serial Number', 'I/O Count'],
      business: ['Support Group', 'Assigned To', 'Cost Center', 'Location']
    }
  },
  { 
    type: 'SENSOR', label: 'Sensor', category: 'OT - Field Devices', icon: <Sensors />, color: '#FF9800',
    expectedProperties: {
      basic: ['CI Class', 'Operational Status', 'Environment', 'Business Criticality'],
      technical: ['IP Address', 'FQDN', 'Firmware Version', 'Model', 'Serial Number', 'Measurement Type'],
      business: ['Support Group', 'Assigned To', 'Cost Center', 'Location']
    }
  },
  { 
    type: 'ACTUATOR', label: 'Actuator', category: 'OT - Field Devices', icon: <SmartToy />, color: '#FF9800',
    expectedProperties: {
      basic: ['CI Class', 'Operational Status', 'Environment', 'Business Criticality'],
      technical: ['IP Address', 'FQDN', 'Firmware Version', 'Model', 'Serial Number', 'Control Type'],
      business: ['Support Group', 'Assigned To', 'Cost Center', 'Location']
    }
  },
  { 
    type: 'MOTOR', label: 'Motor', category: 'OT - Field Devices', icon: <Sync />, color: '#FF9800',
    expectedProperties: {
      basic: ['CI Class', 'Operational Status', 'Environment', 'Business Criticality'],
      technical: ['IP Address', 'FQDN', 'Firmware Version', 'Model', 'Serial Number', 'Power Rating'],
      business: ['Support Group', 'Assigned To', 'Cost Center', 'Location']
    }
  },
  { 
    type: 'VALVE', label: 'Valve', category: 'OT - Field Devices', icon: <FilterAlt />, color: '#FF9800',
    expectedProperties: {
      basic: ['CI Class', 'Operational Status', 'Environment', 'Business Criticality'],
      technical: ['IP Address', 'FQDN', 'Firmware Version', 'Model', 'Serial Number', 'Valve Type'],
      business: ['Support Group', 'Assigned To', 'Cost Center', 'Location']
    }
  },
  
  // --- OT - Industrial Network & Physical ---
  { 
    type: 'INDUSTRIAL_SWITCH', label: 'Industrial Switch', category: 'OT - Industrial Network', icon: <Hub />, color: '#795548',
    expectedProperties: {
      basic: ['CI Class', 'Operational Status', 'Environment', 'Business Criticality'],
      technical: ['Management IP', 'Firmware Version', 'Model', 'Serial Number', 'Port Count'],
      business: ['Support Group', 'Assigned To', 'Cost Center', 'Location']
    }
  },
  { 
    type: 'INDUSTRIAL_GATEWAY', label: 'Industrial Gateway', category: 'OT - Industrial Network', icon: <Lan />, color: '#795548',
    expectedProperties: {
      basic: ['CI Class', 'Operational Status', 'Environment', 'Business Criticality'],
      technical: ['Management IP', 'Firmware Version', 'Model', 'Serial Number', 'Protocol Support'],
      business: ['Support Group', 'Assigned To', 'Cost Center', 'Location']
    }
  },
  { 
    type: 'DATA_DIODE', label: 'Data Diode', category: 'OT - Industrial Network', icon: <East />, color: '#795548',
    expectedProperties: {
      basic: ['CI Class', 'Operational Status', 'Environment', 'Business Criticality'],
      technical: ['Management IP', 'Firmware Version', 'Model', 'Serial Number', 'Direction'],
      business: ['Support Group', 'Assigned To', 'Cost Center', 'Location']
    }
  },
  { 
    type: 'CONTROL_CABINET', label: 'Control Cabinet', category: 'OT - Physical', icon: <Inventory2 />, color: '#607D8B',
    expectedProperties: {
      basic: ['CI Class', 'Operational Status', 'Environment', 'Business Criticality'],
      technical: ['Serial Number', 'Model', 'Dimensions', 'Power Rating', 'Temperature Rating'],
      business: ['Support Group', 'Assigned To', 'Cost Center', 'Location']
    }
  },
  { 
    type: 'PRODUCTION_LINE', label: 'Production Line', category: 'OT - Physical', icon: <Schema />, color: '#607D8B',
    expectedProperties: {
      basic: ['CI Class', 'Operational Status', 'Environment', 'Business Criticality'],
      technical: ['Serial Number', 'Model', 'Capacity', 'Speed', 'Product Type'],
      business: ['Support Group', 'Assigned To', 'Cost Center', 'Location']
    }
  },

  // =================================================================
  // IIoT & Edge Devices
  // =================================================================
  { 
    type: 'IIOT_GATEWAY', label: 'IIoT Gateway', category: 'IIoT & Edge Devices', icon: <RssFeed />, color: '#9C27B0',
    expectedProperties: {
      basic: ['CI Class', 'Operational Status', 'Environment', 'Business Criticality'],
      technical: ['IP Address', 'FQDN', 'Firmware Version', 'Model', 'Serial Number', 'Protocol Support'],
      business: ['Support Group', 'Assigned To', 'Cost Center', 'Location']
    }
  },
  { 
    type: 'EDGE_COMPUTER', label: 'Edge Computer', category: 'IIoT & Edge Devices', icon: <Adb />, color: '#9C27B0',
    expectedProperties: {
      basic: ['CI Class', 'Operational Status', 'Environment', 'Business Criticality'],
      technical: ['IP Address', 'FQDN', 'OS', 'CPU Count', 'RAM', 'Storage', 'Model'],
      business: ['Support Group', 'Assigned To', 'Cost Center', 'Location']
    }
  },
  { 
    type: 'SMART_SENSOR', label: 'Smart Sensor', category: 'IIoT & Edge Devices', icon: <SignalCellularAlt />, color: '#9C27B0',
    expectedProperties: {
      basic: ['CI Class', 'Operational Status', 'Environment', 'Business Criticality'],
      technical: ['IP Address', 'FQDN', 'Firmware Version', 'Model', 'Serial Number', 'Measurement Type'],
      business: ['Support Group', 'Assigned To', 'Cost Center', 'Location']
    }
  },
  { 
    type: 'INDUSTRIAL_ROBOT', label: 'Industrial Robot', category: 'IIoT & Edge Devices', icon: <Construction />, color: '#9C27B0',
    expectedProperties: {
      basic: ['CI Class', 'Operational Status', 'Environment', 'Business Criticality'],
      technical: ['IP Address', 'FQDN', 'Firmware Version', 'Model', 'Serial Number', 'Payload'],
      business: ['Support Group', 'Assigned To', 'Cost Center', 'Location']
    }
  },
  { 
    type: 'AGV', label: 'AGV (Automated Guided Vehicle)', category: 'IIoT & Edge Devices', icon: <Android />, color: '#9C27B0',
    expectedProperties: {
      basic: ['CI Class', 'Operational Status', 'Environment', 'Business Criticality'],
      technical: ['IP Address', 'FQDN', 'Firmware Version', 'Model', 'Serial Number', 'Payload'],
      business: ['Support Group', 'Assigned To', 'Cost Center', 'Location']
    }
  },
  { 
    type: 'ASSET_TRACKER', label: 'Asset Tracker', category: 'IIoT & Edge Devices', icon: <TrackChanges />, color: '#9C27B0',
    expectedProperties: {
      basic: ['CI Class', 'Operational Status', 'Environment', 'Business Criticality'],
      technical: ['IP Address', 'FQDN', 'Firmware Version', 'Model', 'Serial Number', 'Battery Life'],
      business: ['Support Group', 'Assigned To', 'Cost Center', 'Location']
    }
  },
  { 
    type: 'WEARABLE_DEVICE', label: 'Wearable Device', category: 'IIoT & Edge Devices', icon: <Watch />, color: '#9C27B0',
    expectedProperties: {
      basic: ['CI Class', 'Operational Status', 'Environment', 'Business Criticality'],
      technical: ['Firmware Version', 'Model', 'Serial Number', 'Battery Life', 'Connectivity'],
      business: ['Support Group', 'Assigned To', 'Cost Center', 'Location']
    }
  },
  { 
    type: 'VIDEO_CAMERA', label: 'Video Camera', category: 'IIoT & Edge Devices', icon: <Videocam />, color: '#9C27B0',
    expectedProperties: {
      basic: ['CI Class', 'Operational Status', 'Environment', 'Business Criticality'],
      technical: ['IP Address', 'FQDN', 'Firmware Version', 'Model', 'Serial Number', 'Resolution'],
      business: ['Support Group', 'Assigned To', 'Cost Center', 'Location']
    }
  },
  { 
    type: 'NETWORK_SENSOR', label: 'Network Sensor', category: 'IIoT & Edge Devices', icon: <NetworkCheck />, color: '#9C27B0',
    expectedProperties: {
      basic: ['CI Class', 'Operational Status', 'Environment', 'Business Criticality'],
      technical: ['IP Address', 'FQDN', 'Firmware Version', 'Model', 'Serial Number', 'Measurement Type'],
      business: ['Support Group', 'Assigned To', 'Cost Center', 'Location']
    }
  },
  { 
    type: 'BLUETOOTH_DEVICE', label: 'Bluetooth Device', category: 'IIoT & Edge Devices', icon: <Bluetooth />, color: '#9C27B0',
    expectedProperties: {
      basic: ['CI Class', 'Operational Status', 'Environment', 'Business Criticality'],
      technical: ['Firmware Version', 'Model', 'Serial Number', 'Battery Life', 'Range'],
      business: ['Support Group', 'Assigned To', 'Cost Center', 'Location']
    }
  },
  { 
    type: 'CABLE_CONNECTION', label: 'Cable Connection', category: 'IIoT & Edge Devices', icon: <Cable />, color: '#9C27B0',
    expectedProperties: {
      basic: ['CI Class', 'Operational Status', 'Environment', 'Business Criticality'],
      technical: ['Cable Type', 'Length', 'Connector Type', 'Speed', 'Shielding'],
      business: ['Support Group', 'Assigned To', 'Cost Center', 'Location']
    }
  },

  // =================================================================
  // Cloud & Modern Infrastructure
  // =================================================================
  { 
    type: 'CLOUD_FUNCTION', label: 'Cloud Function', category: 'Cloud', icon: <Cloud />, color: '#00695C',
    expectedProperties: {
      basic: ['CI Class', 'Operational Status', 'Environment', 'Business Criticality'],
      technical: ['Function Name', 'Runtime', 'Memory', 'Timeout', 'Trigger Type'],
      business: ['Support Group', 'Assigned To', 'Cost Center', 'Application Owner']
    }
  },
  { 
    type: 'CLOUD_QUEUE', label: 'Cloud Queue', category: 'Cloud', icon: <CloudQueue />, color: '#00695C',
    expectedProperties: {
      basic: ['CI Class', 'Operational Status', 'Environment', 'Business Criticality'],
      technical: ['Queue Name', 'Message TTL', 'Visibility Timeout', 'Max Message Size'],
      business: ['Support Group', 'Assigned To', 'Cost Center', 'Application Owner']
    }
  },
  { 
    type: 'CLOUD_SYNC', label: 'Cloud Sync', category: 'Cloud', icon: <CloudSync />, color: '#00695C',
    expectedProperties: {
      basic: ['CI Class', 'Operational Status', 'Environment', 'Business Criticality'],
      technical: ['Sync Service', 'Source', 'Destination', 'Frequency', 'Conflict Resolution'],
      business: ['Support Group', 'Assigned To', 'Cost Center', 'Data Owner']
    }
  },
  { 
    type: 'DOCKER_CONTAINER', label: 'Docker Container', category: 'Containers', icon: <DevicesIcon />, color: '#00796B',
    expectedProperties: {
      basic: ['CI Class', 'Operational Status', 'Environment', 'Business Criticality'],
      technical: ['Image Name', 'Tag', 'Port Mappings', 'Volume Mounts', 'Host Server'],
      business: ['Support Group', 'Assigned To', 'Cost Center', 'Application Owner']
    }
  },
  { 
    type: 'KUBERNETES_POD', label: 'Kubernetes Pod', category: 'Containers', icon: <Apps />, color: '#00796B',
    expectedProperties: {
      basic: ['CI Class', 'Operational Status', 'Environment', 'Business Criticality'],
      technical: ['Namespace', 'Image', 'Replicas', 'Resource Limits', 'Node'],
      business: ['Support Group', 'Assigned To', 'Cost Center', 'Application Owner']
    }
  },

];

/**
 * A Map for efficient lookups of CI type properties (label, icon, category, color, expectedProperties) by the type key.
 * e.g., ciTypeMap.get('FIREWALL') => { label: 'Firewall', color: '#6A1B9A', expectedProperties: {...}, ... }
 */
export const ciTypeMap = new Map(ciTypes.map(item => [item.type, { ...item, expectedProperties: item.expectedProperties || defaultProperties }]));

/**
 * Get CI type information by type string
 * @param type - The CI type string
 * @returns CI type information or undefined if not found
 */
export const getCITypeInfo = (type: string) => {
  return ciTypeMap.get(type);
};

/**
 * Get all CI types grouped by category
 * @returns Object with categories as keys and arrays of CI types as values
 */
export const getCITypesByCategory = () => {
  const categories: Record<string, typeof ciTypes> = {};
  ciTypes.forEach(ciType => {
    if (!categories[ciType.category]) {
      categories[ciType.category] = [];
    }
    categories[ciType.category].push(ciType);
  });
  return categories;
};

/**
 * Search CI types by label or category
 * @param query - Search query
 * @returns Array of matching CI types
 */
export const searchCITypes = (query: string) => {
  const lowerQuery = query.toLowerCase();
  return ciTypes.filter(ciType => 
    ciType.label.toLowerCase().includes(lowerQuery) ||
    ciType.category.toLowerCase().includes(lowerQuery) ||
    ciType.type.toLowerCase().includes(lowerQuery)
  );
};

/**
 * Get all unique categories
 * @returns Array of unique category names
 */
export const getCategories = () => {
  return Array.from(new Set(ciTypes.map(ci => ci.category))).sort();
};

// Export the CI type strings for TypeScript type generation
export type CITypeString = typeof ciTypes[number]['type'];
